import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/TestAuthContext';
import { useActivityLog } from '../../hooks/useActivityLog';

interface DownloadOptionsCardProps {
  reportId: string;
  hasNarrative: boolean;
  backendUrl: string;
  onShareClick: () => void;
}

export const DownloadOptionsCard: React.FC<DownloadOptionsCardProps> = ({
  reportId,
  hasNarrative,
  backendUrl,
  onShareClick,
}) => {
  const navigate = useNavigate();
  const { logActivity } = useActivityLog();
  const { getToken } = useAuth();

  const handleDownload = async (type: string, url: string) => {
    logActivity({
      reportId,
      actionType: 'download',
      actionDetails: `Downloaded ${type}`,
      section: 'downloads',
      metadata: { download_type: type }
    });
    
    // Use fetch API for more reliable file downloads
    try {
      const token = await getToken();
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Download failed with status ${response.status}`);
      }
      
      const blob = await response.blob();
      const contentDisposition = response.headers.get('content-disposition');
      
      // Determine default filename based on URL/type
      let filename = `export_${reportId}`;
      if (url.includes('format=csv') || type.toLowerCase().includes('csv')) {
        filename += '.csv';
      } else {
        filename += '.xlsx';
      }
      
      // Extract filename from Content-Disposition header if available
      if (contentDisposition) {
        const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (match && match[1]) {
          filename = match[1].replace(/['"]/g, '');
        }
      }
      
      // Create download link
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Download failed:', error);
      console.error(`Download failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  return (
    <>
      {/* PDF Download Options Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold mb-2">📄 Download & Share Options</h2>
          <p className="text-gray-600">Choose the report format or share with others</p>
        </div>
        <button
          onClick={onShareClick}
          className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg shadow transition-colors flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
          </svg>
          Share Report
        </button>
      </div>

      {/* Three Download Options */}
      <div className="grid md:grid-cols-3 gap-6">
        
        {/* Option A: Standard Standard BI Report (Free) */}
        <div className="border-2 border-gray-300 rounded-xl p-6 hover:border-blue-500 transition">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-bold">FREE</span>
          </div>
          <h3 className="text-xl font-bold mb-2">Standard Report</h3>
          <p className="text-gray-600 text-sm mb-4">11-page BI calculation with charts, tables, and methodology</p>
          <ul className="text-sm text-gray-600 space-y-1 mb-4">
            <li>✓ Calculation breakdown</li>
            <li>✓ Statistical validation</li>
            <li>✓ Court-ready format</li>
          </ul>
          <button
            onClick={() => handleDownload('Standard Report PDF', `${backendUrl}/api/v1/download/report-pdf/${reportId}`)}
            className="w-full inline-flex items-center justify-center bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-4 rounded-lg shadow transition"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Download PDF (11 pages)
          </button>
        </div>

        {/* Option B: AI Narrative Report ($150) */}
        <div className="border-2 border-blue-500 rounded-xl p-6 relative overflow-hidden shadow-lg">
          <div className="absolute top-0 right-0 bg-gradient-to-br from-blue-500 to-blue-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
            PREMIUM
          </div>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            </div>
            <span className="bg-blue-600 text-white px-2 py-1 rounded text-xs font-bold">$150</span>
          </div>
          <h3 className="text-xl font-bold mb-2">AI Narrative Report</h3>
          <p className="text-gray-600 text-sm mb-4">30-40 page comprehensive legal narrative for expert witness testimony</p>
          <ul className="text-sm text-gray-600 space-y-1 mb-4">
            <li>✨ 9 comprehensive sections</li>
            <li>✨ Complete causation analysis</li>
            <li>✨ All calculation steps shown</li>
            <li>✨ Professional expert attestation</li>
            <li>✨ 30-40 pages, litigation-ready</li>
          </ul>
          <button
            onClick={() => navigate(`/narrator/${reportId}`)}
            className="w-full bg-gradient-to-r from-blue-600 to-blue-600 hover:from-blue-700 hover:to-blue-700 text-white font-bold py-3 px-4 rounded-lg shadow transition"
          >
            {hasNarrative ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Download Narrative PDF
              </span>
            ) : 'Generate AI Report →'}
          </button>
        </div>

        {/* Option C: Full Analysis Package ($200) */}
        <div className="border-2 border-amber-500 rounded-xl p-6 relative overflow-hidden shadow-lg bg-gradient-to-br from-amber-50 to-orange-50">
          <div className="absolute top-0 right-0 bg-gradient-to-br from-amber-500 to-orange-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
            BEST VALUE
          </div>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
            </div>
            <span className="bg-amber-600 text-white px-2 py-1 rounded text-xs font-bold">$200</span>
          </div>
          <h3 className="text-xl font-bold mb-2">Full Analysis Package</h3>
          <p className="text-gray-600 text-sm mb-4">40+ pages — Complete package with all reports and full statistical analysis</p>
          <ul className="text-sm text-gray-600 space-y-1 mb-4">
            <li className="font-semibold text-amber-800">📄 Standard Report (11 pages):</li>
            <li className="ml-3">• BI calculation breakdown</li>
            <li className="ml-3">• All math & formulas shown</li>
            <li className="font-semibold text-amber-800 mt-2">✨ AI Narrative (30-40 pages):</li>
            <li className="ml-3">• Comprehensive 9-section expert report</li>
            <li className="ml-3">• Litigation-ready language</li>
            <li className="font-semibold text-amber-800 mt-2">📊 Statistical Analysis:</li>
            <li className="ml-3">• Sensitivity (3 parameters)</li>
            <li className="ml-3">• Monte Carlo (1,000 iterations)</li>
            <li className="font-semibold text-amber-800 mt-2">📈 Future Closure (7-8 pages):</li>
            <li className="ml-3">• Pro forma projections & demand</li>
            <li className="ml-3">• Defensibility & methodology</li>
          </ul>
          <button
            onClick={() => navigate(`/narrator/${reportId}`)}
            className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold py-3 px-4 rounded-lg shadow transition"
          >
            {hasNarrative ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Download Full Package
              </span>
            ) : 'Get Full Analysis →'}
          </button>
        </div>
      </div>

      {/* Data Export Row */}
      <div className="mt-6 p-4 bg-gray-50 rounded-xl border border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900">Export Financial Data</h3>
            <p className="text-sm text-gray-600">Download claim data in Excel or CSV format</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => handleDownload('Excel Export', `${backendUrl}/api/v1/export/report/${reportId}/excel`)}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg shadow transition-colors flex items-center gap-2 text-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Excel (.xlsx)
            </button>
          </div>
        </div>
      </div>

      {/* BI Export for Carrier Row */}
      <div className="mt-4 p-4 bg-blue-50 rounded-xl border border-blue-200">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-gray-900">Export for Carrier</h3>
              <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs font-semibold">BI 48</span>
            </div>
            <p className="text-sm text-gray-600">Carrier-ready report with BI 48-category classification</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => handleDownload('BI Excel Export', `${backendUrl}/api/v1/bi/export/${reportId}?format=xlsx`)}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg shadow transition-colors flex items-center gap-2 text-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              BI Excel
            </button>
            <button
              onClick={() => handleDownload('BI CSV Export', `${backendUrl}/api/v1/bi/export/${reportId}?format=csv`)}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white font-semibold rounded-lg shadow transition-colors flex items-center gap-2 text-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              CSV
            </button>
          </div>
        </div>
      </div>

      {/* Expert BI Assessment Report Row */}
      <div className="mt-4 p-4 bg-indigo-50 rounded-xl border border-indigo-200">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-gray-900">Expert BI Assessment Report</h3>
              <span className="bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded text-xs font-semibold">DOCX</span>
            </div>
            <p className="text-sm text-gray-600">Professional expert report with methodology, analysis, and court-ready formatting</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => handleDownload('Expert Report DOCX', `${backendUrl}/api/v1/reports/${reportId}/expert-report`)}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg shadow transition-colors flex items-center gap-2 text-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Expert DOCX
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default DownloadOptionsCard;
